// src/screens/MainScreen.jsx

import React, { useEffect, useState } from "react";
import {
  ArrowUpRight,
  FileText,
  Users,
  Calculator,
  Mail,
  Check,
  Globe,
  ShieldCheck,
} from "lucide-react";

import ProspectForm from "../components/ProspectForm";

/* ======================================================
   IDIOMA COMPARTIDO

   Idiomas: es | en | fr
   Predeterminado: español

   Preparado para el futuro Navbar:

   window.dispatchEvent(
     new CustomEvent("solcont:language-change", {
       detail: { language: "en" },
     })
   );

   También acepta un idioma controlado:
   <MainScreen language="fr" />
====================================================== */

const LANGUAGE_STORAGE_KEY = "solcont-language";
const LANGUAGE_CHANGE_EVENT = "solcont:language-change";
const SUPPORTED_LANGUAGES = ["es", "en", "fr"];

const normalizeLanguage = (value) => {
  if (typeof value !== "string") return null;

  const normalized = value.trim().toLowerCase().split(/[-_]/)[0];

  return SUPPORTED_LANGUAGES.includes(normalized) ? normalized : null;
};

const readStoredLanguage = () => {
  if (typeof window === "undefined") return "es";

  try {
    return (
      normalizeLanguage(
        window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
      ) || "es"
    );
  } catch {
    return "es";
  }
};

const useMainScreenLanguage = (controlledLanguage) => {
  const [storedLanguage, setStoredLanguage] = useState(readStoredLanguage);

  useEffect(() => {
    const handleLanguageChange = (event) => {
      const value =
        typeof event.detail === "string"
          ? event.detail
          : event.detail?.language;

      const nextLanguage = normalizeLanguage(value);

      if (!nextLanguage) return;

      setStoredLanguage(nextLanguage);

      try {
        window.localStorage.setItem(
          LANGUAGE_STORAGE_KEY,
          nextLanguage
        );
      } catch {
        // El cambio sigue funcionando aunque no haya almacenamiento.
      }
    };

    const handleStorageChange = (event) => {
      if (
        event.key === LANGUAGE_STORAGE_KEY ||
        event.key === null
      ) {
        setStoredLanguage(readStoredLanguage());
      }
    };

    window.addEventListener(
      LANGUAGE_CHANGE_EVENT,
      handleLanguageChange
    );

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener(
        LANGUAGE_CHANGE_EVENT,
        handleLanguageChange
      );

      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return normalizeLanguage(controlledLanguage) || storedLanguage;
};

/* ======================================================
   IDENTIDAD VISUAL
====================================================== */

const BRAND = {
  blue: "#1648EE",
  navy: "#081B3E",
  ivory: "#F7F3E9",
  magenta: "#F72585",
};

const GRID_STYLE = {
  backgroundColor: BRAND.blue,
  backgroundImage: `
    linear-gradient(
      rgba(255,255,255,0.065) 1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(255,255,255,0.065) 1px,
      transparent 1px
    )
  `,
  backgroundSize: "64px 64px",
};

const DISPLAY_STYLE = {
  fontFamily: '"Arial Narrow", "Roboto Condensed", Arial, sans-serif',
  fontStretch: "condensed",
};


/* ======================================================
   ESCALA RESPONSIVA, AISLADA A ESTA PANTALLA

   El navbar de App ocupa 4rem y App ya aporta el pt-16.
   Aquí NO se vuelve a compensar esa altura.
   No se fijan alturas ni se recorta contenido para hacerlo caber.
   Los estilos viajan con este archivo: no requieren otro CSS.
====================================================== */

const RESPONSIVE_STYLES = `
  .solcont-main {
    --sc-header-offset: 4rem;
    --sc-section-space: clamp(2.5rem, 6vh, 5rem);
    --sc-hero-space: clamp(1.75rem, 5vh, 4rem);
    --sc-stack-space: clamp(1rem, 2.5vh, 1.75rem);
    --sc-gap: clamp(1.5rem, 3vw, 3.5rem);
    --sc-panel-space: clamp(1.25rem, 2.2vw, 2rem);
  }

  .solcont-main .sc-container {
    width: 100%;
    max-width: 75rem;
    margin-inline: auto;
    padding-inline: clamp(1rem, 3vw, 2.5rem);
  }
  .solcont-main .sc-section { padding-block: var(--sc-section-space); }
  .solcont-main section[id] {
    scroll-margin-top: calc(var(--sc-header-offset) + 1rem);
  }
  .solcont-main .sc-split,
  .solcont-main .sc-heading {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--sc-gap);
  }
  .solcont-main .sc-hero { padding-block: var(--sc-hero-space); }
  .solcont-main .sc-accent { margin-top: 1rem; }
  .solcont-main .sc-title {
    font-size: clamp(1.875rem, 3.6vw, 3.125rem);
    line-height: 1.06;
    text-wrap: balance;
    overflow-wrap: anywhere;
  }
  .solcont-main .sc-hero-title {
    margin-top: var(--sc-stack-space);
    font-size: clamp(2rem, 7vw, 3.5rem);
    line-height: 1.02;
    text-wrap: balance;
    overflow-wrap: anywhere;
  }
  .solcont-main .sc-copy {
    margin-top: var(--sc-stack-space);
    font-size: 1rem;
    line-height: 1.6;
  }
  .solcont-main .sc-heading .sc-copy { max-width: 42rem; }
  .solcont-main .sc-spaced { margin-top: var(--sc-stack-space); }
  .solcont-main .sc-actions {
    margin-top: var(--sc-stack-space);
    gap: 0.75rem;
  }
  .solcont-main .sc-button {
    min-height: 2.75rem;
    max-width: 100%;
    padding: 0.75rem 1.25rem;
    line-height: 1.4;
    text-align: center;
    white-space: normal;
  }
  .solcont-main .sc-trust {
    margin-top: var(--sc-stack-space);
    gap: 0.625rem 1.25rem;
    line-height: 1.5;
  }
  .solcont-main .sc-trust > span { min-width: 0; }
  .solcont-main .sc-panel { padding: var(--sc-panel-space); }
  .solcont-main .sc-panel-list { margin-top: 1.25rem; }
  .solcont-main .sc-panel-row {
    grid-template-columns: 1.5rem minmax(0, 1fr);
    gap: 0.75rem;
    padding-block: 0.875rem;
  }
  .solcont-main .sc-panel-item {
    font-size: clamp(1.125rem, 2.2vw, 1.625rem);
    overflow-wrap: anywhere;
  }
  .solcont-main .sc-panel-note { margin-top: 1rem; padding: 1rem; }
  .solcont-main .sc-panel-note > p + p { margin-top: 0.5rem; }
  .solcont-main .sc-audience { min-height: 3.5rem; padding-block: 0.75rem; }
  .solcont-main .sc-services-grid { margin-top: var(--sc-gap); gap: 1.25rem; }
  .solcont-main .sc-service-card { padding: var(--sc-panel-space); }
  .solcont-main .sc-service-card > div { padding-bottom: 1rem; }
  .solcont-main .sc-service-title {
    margin-top: 1.25rem;
    font-size: clamp(1.25rem, 2vw, 1.625rem);
    line-height: 1.15;
    overflow-wrap: anywhere;
  }
  .solcont-main .sc-service-card > p { margin-top: 0.875rem; line-height: 1.6; }
  .solcont-main .sc-service-card > ul { margin-top: 1.25rem; }
  .solcont-main .sc-service-card li { line-height: 1.5; }
  .solcont-main .sc-service-card li + li { margin-top: 0.625rem; }
  .solcont-main .sc-payment-number {
    font-size: clamp(5rem, 10.5vh, 8rem);
    line-height: 0.85;
  }
  .solcont-main .sc-payment-title {
    font-size: clamp(1.375rem, 2.4vw, 1.875rem);
    line-height: 1.1;
    overflow-wrap: anywhere;
  }
  .solcont-main .sc-payment-card > p { line-height: 1.5; }
  .solcont-main .sc-payment-divider { margin-top: 1rem; padding-top: 1rem; }
  .solcont-main .sc-process-step { padding-block: 1.25rem; }
  .solcont-main .sc-process-step > p { line-height: 1.6; }
  .solcont-main .sc-team-card { padding: var(--sc-panel-space); }
  .solcont-main .sc-team-avatar { width: 4rem; height: 4rem; }
  .solcont-main .sc-form-heading { margin-bottom: 1.25rem; padding-bottom: 1.25rem; }

  @supports (height: 100svh) {
    .solcont-main {
      --sc-section-space: clamp(2.5rem, 6svh, 5rem);
      --sc-hero-space: clamp(1.75rem, 5svh, 4rem);
      --sc-stack-space: clamp(1rem, 2.5svh, 1.75rem);
    }
    .solcont-main .sc-payment-number { font-size: clamp(5rem, 10.5svh, 8rem); }
  }

  /* Dos columnas cuando hay ancho real, no por tipo de dispositivo. */
  @media (min-width: 60rem) {
    .solcont-main .sc-split { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
    .solcont-main .sc-hero { grid-template-columns: minmax(0, 1.25fr) minmax(0, 0.85fr); }
    .solcont-main .sc-regularization { grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr); }
    .solcont-main .sc-process,
    .solcont-main .sc-contact { grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr); }
    .solcont-main .sc-heading { grid-template-columns: minmax(0, 0.65fr) minmax(0, 1.35fr); }
    .solcont-main .sc-hero-title {
      font-size: clamp(2.375rem, min(4.2vw, 7.2vh), 4.25rem);
    }
    .solcont-main .sc-title {
      font-size: clamp(1.875rem, min(3.6vw, 5.8vh), 3.125rem);
    }
    .solcont-main .sc-audiences { grid-template-columns: repeat(4, minmax(0, 1fr)); }
    .solcont-main .sc-audience { border-bottom: 0; }
    @supports (height: 100svh) {
      .solcont-main .sc-hero-title {
        font-size: clamp(2.375rem, min(4.2vw, 7.2svh), 4.25rem);
      }
      .solcont-main .sc-title {
        font-size: clamp(1.875rem, min(3.6vw, 5.8svh), 3.125rem);
      }
    }
  }

  /* Laptops y ventanas bajas: comprimir espacios, no el contenido. */
  @media (min-width: 60rem) and (max-height: 46.25rem) {
    .solcont-main {
      --sc-section-space: 2rem;
      --sc-hero-space: 1.5rem;
      --sc-stack-space: 1rem;
      --sc-panel-space: 1.25rem;
    }
    .solcont-main .sc-hero-title {
      font-size: clamp(2.25rem, min(4.2vw, 6.8vh), 3.25rem);
    }
    .solcont-main .sc-panel-list { margin-top: 1rem; }
    .solcont-main .sc-panel-row { padding-block: 0.625rem; }
    .solcont-main .sc-panel-item { font-size: 1.375rem; line-height: 1.2; }
    .solcont-main .sc-panel-note { margin-top: 0.75rem; padding: 0.75rem; }
    .solcont-main .sc-panel-note > p { line-height: 1.5; }
  }

  /* Los formularios y los textos largos conservan su altura natural.
     En móvil o con zoom no hay scale(), height:100vh ni scroll interno. */
`;

const BUTTON_BASE = [
  "sc-button inline-flex items-center justify-center gap-3",
  "rounded-xl text-sm font-bold",
  "transition duration-200",
  "hover:-translate-y-0.5 active:translate-y-0",
  "focus-visible:outline-none focus-visible:ring-2",
  "focus-visible:ring-[#F72585] focus-visible:ring-offset-4",
  "motion-reduce:transform-none motion-reduce:transition-none",
].join(" ");

/* ======================================================
   DATOS QUE NO CAMBIAN CON EL IDIOMA
====================================================== */

const TEAM = [
  {
    id: "isaias",
    name: "Isaías Lezama Aponte",
    initials: "IL",
    email: "isaias.lezama@solcont.com.mx",
    avatarUrl: "/media/isaiasLezama.jpg",
  },
  {
    id: "elizabeth",
    name: "Elizabeth Zavaleta Castillo",
    initials: "EZ",
    email: "elizabeth.zavaleta@solcont.com.mx",
    avatarUrl: "/media/elizabethZavaleta.jpg",
  },
];

const SERVICE_ICONS = [
  FileText,
  ShieldCheck,
  Calculator,
  Users,
];

/* ======================================================
   TRADUCCIONES
====================================================== */

const CONTENT = {
  es: {
    hero: {
      eyebrow: "SOLCONT / CONTABILIDAD + ESTRATEGIA FISCAL",
      title: ["Tus números claros.", "Tus decisiones también."],
      description:
        "Acompañamos a personas, profesionistas y negocios a cumplir, ordenar su operación y tomar mejores decisiones fiscales.",
      primary: "Solicitar asesoría",
      secondary: "Conocer servicios",
      trust: [
        "Atención personalizada",
        "Servicio en línea en todo México",
      ],
      panelLabel: "EL ENFOQUE SOLCONT",
      panelItems: ["Diagnóstico", "Orden", "Estrategia"],
      panelNoteTitle: "NO SE TRATA SOLO DE DECLARAR.",
      panelNote:
        "Se trata de saber dónde estás y qué sigue.",
    },

    audiences: [
      "Profesionistas independientes",
      "Emprendedores y negocios",
      "Personas físicas y morales",
      "Mexicanos y extranjeros",
    ],

    services: {
      eyebrow: "SOLUCIONES PARA TU OPERACIÓN",
      title: "Contabilidad que te ayuda a avanzar.",
      description:
        "Cada servicio parte de entender tu situación real y definir un alcance adecuado para tu operación.",
      items: [
        {
          title: "Contabilidad y cumplimiento",
          description:
            "Registros, declaraciones y control contable con información clara para tomar decisiones.",
          points: [
            "Declaraciones mensuales y anuales",
            "Conciliaciones bancarias",
            "Papeles de trabajo y seguimiento",
          ],
        },
        {
          title: "Regularización fiscal",
          description:
            "Identificamos obligaciones pendientes y construimos una ruta para poner tu situación en orden.",
          points: [
            "Diagnóstico de situación fiscal",
            "Declaraciones pendientes",
            "Acompañamiento en trámites ante el SAT",
          ],
        },
        {
          title: "Estrategia fiscal",
          description:
            "Analizamos tu operación y el impacto fiscal de tus decisiones con enfoque en el cumplimiento.",
          points: [
            "Diagnóstico y planeación",
            "Revisión de deducciones",
            "Proyección de impuestos",
          ],
        },
        {
          title: "Nómina e IMSS",
          description:
            "Administración de nómina y obligaciones patronales para acompañar la operación de tu negocio.",
          points: [
            "Cálculo y timbrado de nómina",
            "SUA, SIPARE e INFONAVIT",
            "Atención de auditorías",
          ],
        },
      ],
    },

    regularization: {
      eyebrow: "SOLCONT / REGULARIZACIÓN FISCAL",
      title: "Dejarlo pasar no hace que desaparezca.",
      description:
        "Revisamos tus obligaciones, identificamos pendientes y te acompañamos durante el proceso de regularización ante el SAT.",
      cta: "Revisar mi situación",
      cardLabel: "FACILIDADES DE PAGO",
      upTo: "HASTA",
      installments: "MESES SIN INTERESES",
      cards: "Con tarjetas de crédito participantes.",
      fees: "En los honorarios de tu regularización.",
      conditions:
        "Aplica a honorarios de SolCont, no al pago de impuestos. Consulta tarjetas participantes y condiciones.",
    },

    process: {
      eyebrow: "UN MÉTODO CLARO",
      title: "Primero entendemos. Después resolvemos.",
      description:
        "Conoce cómo comenzamos y qué puedes esperar durante el proceso.",
      steps: [
        {
          title: "Diagnóstico",
          description:
            "Analizamos tus CFDI, movimientos bancarios y situación fiscal actual.",
        },
        {
          title: "Ruta de trabajo",
          description:
            "Definimos prioridades, alcances, tiempos y entregables.",
        },
        {
          title: "Seguimiento",
          description:
            "Ejecutamos el plan y mantenemos comunicación durante el proceso.",
        },
      ],
    },

    team: {
      eyebrow: "SOLCONT / QUIÉNES SOMOS",
      title: "Experiencia fiscal con visión de negocio.",
      description:
        "Combinamos experiencia operativa, consultoría y herramientas digitales para dar seguimiento a cada caso.",
      roles: {
        isaias: "Soluciones fiscales y digitales",
        elizabeth: "Operaciones contables y fiscales",
      },
      photoAlt: "Fotografía de",
      emailLabel: "Enviar correo a",
    },

    contact: {
      eyebrow: "HABLEMOS DE TU SITUACIÓN",
      title: "El primer paso es entender qué necesitas.",
      description:
        "Cuéntanos a qué te dedicas y qué necesitas resolver. Revisaremos tu solicitud para definir el siguiente paso.",
      benefits: [
        "Atención personalizada",
        "Modalidad en línea",
        "Personas físicas y morales",
        "PFAE y RESICO",
      ],
      formTitle: "Cuéntanos tu caso",
      formDescription:
        "Completa el formulario para iniciar el contacto con nuestro equipo.",
    },
  },

  en: {
    hero: {
      eyebrow: "SOLCONT / ACCOUNTING + TAX STRATEGY",
      title: ["Clear numbers.", "Confident decisions."],
      description:
        "We help individuals, independent professionals and businesses stay compliant, organize their operations and make informed tax decisions in Mexico.",
      primary: "Request a consultation",
      secondary: "Explore our services",
      trust: [
        "Personalized support",
        "Online service throughout Mexico",
      ],
      panelLabel: "THE SOLCONT APPROACH",
      panelItems: ["Assessment", "Organization", "Strategy"],
      panelNoteTitle: "IT IS NOT JUST ABOUT FILING.",
      panelNote:
        "It is about understanding where you stand and what comes next.",
    },

    audiences: [
      "Independent professionals",
      "Entrepreneurs and businesses",
      "Individuals and companies",
      "Mexican and foreign clients",
    ],

    services: {
      eyebrow: "SOLUTIONS FOR YOUR OPERATIONS",
      title: "Accounting that helps you move forward.",
      description:
        "Every service starts with understanding your situation and defining the right scope for your operations.",
      items: [
        {
          title: "Accounting and compliance",
          description:
            "Bookkeeping, tax returns and accounting oversight that give you clear information for better decisions.",
          points: [
            "Monthly and annual tax returns",
            "Bank reconciliations",
            "Supporting schedules and follow-up",
          ],
        },
        {
          title: "Tax compliance catch-up",
          description:
            "We identify outstanding obligations and create a clear plan to bring your tax affairs up to date.",
          points: [
            "Tax compliance assessment",
            "Outstanding tax returns",
            "Support with SAT procedures",
          ],
        },
        {
          title: "Tax strategy",
          description:
            "We review your operations and the tax implications of your decisions with a focus on compliance.",
          points: [
            "Assessment and planning",
            "Review of deductions",
            "Tax projections",
          ],
        },
        {
          title: "Payroll and IMSS",
          description:
            "Payroll administration and employer obligations to support your day-to-day business operations.",
          points: [
            "Payroll calculations and electronic receipts",
            "SUA, SIPARE and INFONAVIT",
            "Audit support",
          ],
        },
      ],
    },

    regularization: {
      eyebrow: "SOLCONT / TAX COMPLIANCE",
      title: "Putting it off will not make it go away.",
      description:
        "We review your obligations, identify outstanding filings and support you through the process of bringing your tax affairs up to date with the SAT.",
      cta: "Review my situation",
      cardLabel: "FLEXIBLE PAYMENT OPTIONS",
      upTo: "UP TO",
      installments: "INTEREST-FREE MONTHLY PAYMENTS",
      cards: "With participating credit cards.",
      fees: "For your tax compliance service fees.",
      conditions:
        "Applies to SolCont service fees, not tax payments. Ask about participating cards and terms.",
    },

    process: {
      eyebrow: "A CLEAR PROCESS",
      title: "First we understand. Then we act.",
      description:
        "Know how we get started and what to expect throughout the process.",
      steps: [
        {
          title: "Assessment",
          description:
            "We review your CFDI electronic invoices, bank activity and current tax situation.",
        },
        {
          title: "Action plan",
          description:
            "We define priorities, scope, timelines and deliverables.",
        },
        {
          title: "Follow-up",
          description:
            "We carry out the plan and keep you informed throughout the process.",
        },
      ],
    },

    team: {
      eyebrow: "SOLCONT / ABOUT US",
      title: "Tax expertise with a business perspective.",
      description:
        "We combine operational experience, advisory services and digital tools to follow through on each case.",
      roles: {
        isaias: "Tax and digital solutions",
        elizabeth: "Accounting and tax operations",
      },
      photoAlt: "Portrait of",
      emailLabel: "Email",
    },

    contact: {
      eyebrow: "LET US TALK ABOUT YOUR SITUATION",
      title: "The first step is understanding what you need.",
      description:
        "Tell us about your work or business and what you need help with. We will review your request to determine the next step.",
      benefits: [
        "Personalized support",
        "Online service",
        "Individuals and companies",
        "PFAE and RESICO",
      ],
      formTitle: "Tell us about your situation",
      formDescription:
        "Complete the form to get in touch with our team.",
    },
  },

  fr: {
    hero: {
      eyebrow: "SOLCONT / COMPTABILITÉ + STRATÉGIE FISCALE",
      title: ["Des chiffres clairs.", "Des décisions éclairées."],
      description:
        "Nous accompagnons les particuliers, les professionnels indépendants et les entreprises dans leurs obligations comptables et fiscales au Mexique.",
      primary: "Demander une consultation",
      secondary: "Découvrir nos services",
      trust: [
        "Accompagnement personnalisé",
        "Services en ligne partout au Mexique",
      ],
      panelLabel: "L’APPROCHE SOLCONT",
      panelItems: ["Diagnostic", "Organisation", "Stratégie"],
      panelNoteTitle:
        "IL NE S’AGIT PAS SEULEMENT DE DÉCLARER.",
      panelNote:
        "Il s’agit de savoir où vous en êtes et quelle est la prochaine étape.",
    },

    audiences: [
      "Professionnels indépendants",
      "Entrepreneurs et commerces",
      "Particuliers et sociétés",
      "Clients mexicains et étrangers",
    ],

    services: {
      eyebrow: "DES SOLUTIONS POUR VOTRE ACTIVITÉ",
      title: "Une comptabilité qui vous aide à avancer.",
      description:
        "Chaque service commence par l’analyse de votre situation et la définition d’un accompagnement adapté à votre activité.",
      items: [
        {
          title: "Comptabilité et conformité",
          description:
            "Tenue comptable, déclarations et suivi pour disposer d’informations claires et prendre de meilleures décisions.",
          points: [
            "Déclarations mensuelles et annuelles",
            "Rapprochements bancaires",
            "Dossiers de travail et suivi",
          ],
        },
        {
          title: "Régularisation fiscale",
          description:
            "Nous identifions les obligations en attente et préparons un plan pour mettre votre situation fiscale à jour.",
          points: [
            "Diagnostic de votre situation fiscale",
            "Déclarations en retard",
            "Accompagnement auprès du SAT",
          ],
        },
        {
          title: "Stratégie fiscale",
          description:
            "Nous analysons votre activité et les conséquences fiscales de vos décisions dans le respect de vos obligations.",
          points: [
            "Diagnostic et planification",
            "Examen des déductions",
            "Prévisions fiscales",
          ],
        },
        {
          title: "Paie et IMSS",
          description:
            "Gestion de la paie et des obligations de l’employeur pour soutenir le fonctionnement de votre entreprise.",
          points: [
            "Calcul de la paie et émission des CFDI",
            "SUA, SIPARE et INFONAVIT",
            "Accompagnement lors des contrôles",
          ],
        },
      ],
    },

    regularization: {
      eyebrow: "SOLCONT / RÉGULARISATION FISCALE",
      title: "Reporter ne fait pas disparaître le problème.",
      description:
        "Nous examinons vos obligations, identifions les déclarations en attente et vous accompagnons dans votre régularisation auprès du SAT.",
      cta: "Faire le point sur ma situation",
      cardLabel: "FACILITÉS DE PAIEMENT",
      upTo: "JUSQU’À",
      installments: "MENSUALITÉS SANS INTÉRÊTS",
      cards: "Avec les cartes de crédit participantes.",
      fees: "Pour les honoraires de votre régularisation.",
      conditions:
        "Offre applicable aux honoraires de SolCont, et non au paiement des impôts. Renseignez-vous sur les cartes participantes et les conditions.",
    },

    process: {
      eyebrow: "UNE MÉTHODE CLAIRE",
      title: "Comprendre d’abord. Agir ensuite.",
      description:
        "Découvrez comment nous commençons et à quoi vous attendre pendant l’accompagnement.",
      steps: [
        {
          title: "Diagnostic",
          description:
            "Nous examinons vos factures électroniques CFDI, vos mouvements bancaires et votre situation fiscale.",
        },
        {
          title: "Plan d’action",
          description:
            "Nous définissons les priorités, le périmètre, les délais et les livrables.",
        },
        {
          title: "Suivi",
          description:
            "Nous mettons le plan en œuvre et vous tenons informé tout au long du processus.",
        },
      ],
    },

    team: {
      eyebrow: "SOLCONT / NOTRE ÉQUIPE",
      title: "L’expertise fiscale au service de votre activité.",
      description:
        "Nous associons expérience opérationnelle, conseil et outils numériques pour assurer le suivi de chaque dossier.",
      roles: {
        isaias: "Solutions fiscales et numériques",
        elizabeth: "Opérations comptables et fiscales",
      },
      photoAlt: "Portrait de",
      emailLabel: "Envoyer un courriel à",
    },

    contact: {
      eyebrow: "PARLONS DE VOTRE SITUATION",
      title: "Tout commence par comprendre vos besoins.",
      description:
        "Présentez-nous votre activité et les questions que vous souhaitez résoudre. Nous examinerons votre demande pour définir la prochaine étape.",
      benefits: [
        "Accompagnement personnalisé",
        "Services en ligne",
        "Particuliers et sociétés",
        "PFAE et RESICO",
      ],
      formTitle: "Présentez-nous votre situation",
      formDescription:
        "Remplissez le formulaire pour prendre contact avec notre équipe.",
    },
  },
};

/* ======================================================
   COMPONENTES VISUALES
====================================================== */

const AccentRule = () => (
  <span
    aria-hidden="true"
    className="sc-accent block h-1 w-16 rounded-full bg-[#F72585]"
  />
);

const Eyebrow = ({ children, light = false }) => (
  <p
    className={[
      "text-[11px] font-bold uppercase leading-relaxed",
      "tracking-[0.16em] sm:text-xs",
      light ? "text-blue-100" : "text-[#1648EE]",
    ].join(" ")}
  >
    {children}
  </p>
);

const BrandOrbits = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 overflow-hidden"
  >
    <div className="absolute -right-64 -top-48 h-[560px] w-[560px] rounded-full border border-white/35 sm:-right-48 sm:h-[700px] sm:w-[700px]" />

    <div className="absolute -right-40 -top-16 h-[350px] w-[350px] rounded-full border border-white/35 sm:-right-24 sm:h-[450px] sm:w-[450px]" />

    <span className="absolute right-28 top-24 h-5 w-5 rounded-full bg-[#F72585] sm:right-64 sm:top-28 sm:h-6 sm:w-6" />
  </div>
);

const SectionHeading = ({ eyebrow, title, description }) => (
  <div className="sc-heading grid">
    <div>
      <Eyebrow>{eyebrow}</Eyebrow>
      <AccentRule />
    </div>

    <div className="min-w-0">
      <h2
        style={DISPLAY_STYLE}
        className="sc-title max-w-3xl font-black uppercase tracking-[-0.035em]"
      >
        {title}
      </h2>

      {description && (
        <p className="sc-copy max-w-2xl text-slate-600">
          {description}
        </p>
      )}
    </div>
  </div>
);

const ServiceCard = ({
  icon: Icon,
  index,
  title,
  description,
  points,
}) => (
  <article className="sc-service-card flex h-full min-w-0 flex-col rounded-2xl border border-[#081B3E]/10 bg-white transition duration-200 hover:-translate-y-1 hover:border-[#1648EE]/25 hover:shadow-xl hover:shadow-blue-950/5 motion-reduce:transform-none">
    <div className="flex items-center justify-between border-b border-[#081B3E]/10 pb-5">
      <span className="text-xs font-bold tracking-widest text-[#F72585]">
        {String(index).padStart(2, "0")}
      </span>

      <Icon
        aria-hidden="true"
        className="h-7 w-7 text-[#1648EE]"
        strokeWidth={1.7}
      />
    </div>

    <h3
      style={DISPLAY_STYLE}
      className="sc-service-title font-black uppercase tracking-tight"
    >
      {title}
    </h3>

    <p className="mt-4 text-sm leading-7 text-slate-600">
      {description}
    </p>

    <ul className="mt-7 space-y-3">
      {points.map((point) => (
        <li
          key={point}
          className="flex items-start gap-3 text-sm leading-6"
        >
          <Check
            aria-hidden="true"
            className="mt-1 h-4 w-4 shrink-0 text-[#F72585]"
          />

          <span>{point}</span>
        </li>
      ))}
    </ul>
  </article>
);

const ProcessStep = ({ index, title, description }) => (
  <article className="sc-process-step grid grid-cols-[36px_minmax(0,1fr)] gap-x-4 border-b border-[#081B3E]/15 sm:grid-cols-[42px_minmax(0,0.8fr)_minmax(0,1fr)] sm:gap-x-6">
    <span className="pt-1 text-xs font-bold tracking-widest text-[#F72585]">
      {String(index).padStart(2, "0")}
    </span>

    <h3
      style={DISPLAY_STYLE}
      className="text-xl font-bold uppercase leading-tight tracking-tight"
    >
      {title}
    </h3>

    <p className="col-start-2 mt-3 text-sm leading-7 text-slate-600 sm:col-start-auto sm:mt-0">
      {description}
    </p>
  </article>
);

const TeamCard = ({ member, role, copy }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <article className="sc-team-card min-w-0 rounded-2xl border border-white/15 bg-white/5">
      <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
        <div className="sc-team-avatar flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#F7F3E9] ring-1 ring-white/20">
          {imageError ? (
            <span
              aria-hidden="true"
              className="text-xl font-black text-[#1648EE]"
            >
              {member.initials}
            </span>
          ) : (
            <img
              src={member.avatarUrl}
              alt={`${copy.photoAlt} ${member.name}`}
              width={80}
              height={80}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
              onError={() => setImageError(true)}
            />
          )}
        </div>

        <div className="min-w-0">
          <h3 className="text-lg font-bold leading-snug text-white">
            {member.name}
          </h3>

          <p className="mt-2 text-sm leading-6 text-blue-100/80">
            {role}
          </p>

          <a
            href={`mailto:${member.email}`}
            aria-label={`${copy.emailLabel} ${member.name}`}
            className="mt-4 inline-flex max-w-full items-start gap-2 rounded text-xs leading-6 text-white/80 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F72585]"
          >
            <Mail
              aria-hidden="true"
              className="mt-1 h-4 w-4 shrink-0 text-[#F72585]"
            />

            <span className="break-all">{member.email}</span>
          </a>
        </div>
      </div>
    </article>
  );
};

/* ======================================================
   MAIN SCREEN
====================================================== */

const MainScreen = ({ language: controlledLanguage } = {}) => {
  const language = useMainScreenLanguage(controlledLanguage);
  const t = CONTENT[language];

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const scrollToForm = () => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    document.getElementById("asesoria")?.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  return (
    <div
      lang={language}
      className="solcont-main bg-[#F7F3E9] text-[#081B3E]"
    >
      <style>{RESPONSIVE_STYLES}</style>

      {/* ==================================================
         HERO
      ================================================== */}

      <section
        id="inicio"
        className="relative isolate scroll-mt-24 overflow-hidden text-[#F7F3E9]"
        style={GRID_STYLE}
      >
        <BrandOrbits />

        <div className="sc-container sc-split sc-hero relative z-10 grid items-center">
          <div className="min-w-0">
            <Eyebrow light>{t.hero.eyebrow}</Eyebrow>
            <AccentRule />

            <h1
              style={DISPLAY_STYLE}
              className="sc-hero-title font-black uppercase tracking-[-0.04em]"
            >
              {t.hero.title.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </h1>

            <p className="sc-copy max-w-xl text-white/85">
              {t.hero.description}
            </p>

            <div className="sc-actions flex flex-col sm:flex-row sm:flex-wrap">
              <button
                type="button"
                onClick={scrollToForm}
                className={`${BUTTON_BASE} bg-[#F72585] text-white hover:bg-[#DE1672] focus-visible:ring-offset-[#1648EE]`}
              >
                {t.hero.primary}

                <ArrowUpRight
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0"
                />
              </button>

              <a
                href="#servicios"
                className={`${BUTTON_BASE} border border-white/35 text-white hover:bg-white/10 focus-visible:ring-offset-[#1648EE]`}
              >
                {t.hero.secondary}
              </a>
            </div>

            <div className="sc-trust flex flex-col text-xs text-white/85 sm:flex-row sm:flex-wrap">
              <span className="flex items-center gap-2">
                <ShieldCheck
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0"
                />

                {t.hero.trust[0]}
              </span>

              <span className="flex items-center gap-2">
                <Globe
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0"
                />

                {t.hero.trust[1]}
              </span>
            </div>
          </div>

          <aside
            aria-label={t.hero.panelLabel}
            className="sc-panel min-w-0 rounded-3xl bg-[#F7F3E9] text-[#081B3E] shadow-2xl shadow-blue-950/20"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#1648EE]">
              {t.hero.panelLabel}
            </p>

            <div className="sc-panel-list">
              {t.hero.panelItems.map((item, index) => (
                <div
                  key={item}
                  className="sc-panel-row grid items-center border-t border-[#081B3E]/15"
                >
                  <span className="text-xs font-bold text-[#F72585]">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <p
                    style={DISPLAY_STYLE}
                    className="sc-panel-item font-black uppercase leading-tight tracking-tight"
                  >
                    {item}
                  </p>
                </div>
              ))}
            </div>

            <div className="sc-panel-note rounded-xl bg-[#081B3E] text-white">
              <p className="text-[10px] font-bold uppercase leading-5 tracking-wider text-pink-300">
                {t.hero.panelNoteTitle}
              </p>

              <p className="mt-3 text-sm font-semibold leading-6">
                {t.hero.panelNote}
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* ==================================================
         A QUIÉNES ATENDEMOS
      ================================================== */}

      <div className="bg-[#081B3E] text-white">
        <ul className="sc-container sc-audiences grid grid-cols-1 sm:grid-cols-2">
          {t.audiences.map((audience) => (
            <li
              key={audience}
              className="sc-audience flex items-center gap-3 border-b border-white/10 text-xs font-semibold leading-5 sm:px-4"
            >
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#F72585]"
              />

              {audience}
            </li>
          ))}
        </ul>
      </div>

      {/* ==================================================
         SERVICIOS
      ================================================== */}

      <section
        id="servicios"
        className="sc-container sc-section"
      >
        <SectionHeading
          eyebrow={t.services.eyebrow}
          title={t.services.title}
          description={t.services.description}
        />

        <div className="sc-services-grid grid md:grid-cols-2">
          {t.services.items.map((service, index) => (
            <ServiceCard
              key={index}
              index={index + 1}
              icon={SERVICE_ICONS[index]}
              {...service}
            />
          ))}
        </div>
      </section>

      {/* ==================================================
         REGULARIZACIÓN / MESES SIN INTERESES
      ================================================== */}

      <section
        id="regularizacion"
        className="relative isolate scroll-mt-24 overflow-hidden text-white"
        style={GRID_STYLE}
      >
        <BrandOrbits />

        <div className="sc-container sc-section sc-split sc-regularization relative z-10 grid items-center">
          <div className="min-w-0">
            <Eyebrow light>
              {t.regularization.eyebrow}
            </Eyebrow>

            <AccentRule />

            <h2
              style={DISPLAY_STYLE}
              className="sc-title sc-spaced font-black uppercase tracking-[-0.035em]"
            >
              {t.regularization.title}
            </h2>

            <p className="sc-copy max-w-xl text-white/85">
              {t.regularization.description}
            </p>

            <button
              type="button"
              onClick={scrollToForm}
              className={`${BUTTON_BASE} sc-spaced bg-[#F7F3E9] text-[#1648EE] hover:bg-white focus-visible:ring-offset-[#1648EE]`}
            >
              {t.regularization.cta}

              <ArrowUpRight
                aria-hidden="true"
                className="h-4 w-4 shrink-0"
              />
            </button>
          </div>

          <div className="sc-panel sc-payment-card min-w-0 rounded-3xl bg-[#F7F3E9] text-[#081B3E] shadow-2xl shadow-blue-950/20">
            <div className="flex items-center gap-4">
              <span
                aria-hidden="true"
                className="h-3 w-3 shrink-0 rounded-full bg-[#F72585]"
              />

              <span
                aria-hidden="true"
                className="h-px flex-1 bg-[#081B3E]/60"
              />
            </div>

            <p className="sc-spaced text-[11px] font-bold uppercase tracking-[0.15em] text-[#1648EE]">
              {t.regularization.cardLabel}
            </p>

            <div className="sc-spaced text-[#1648EE]">
              <span className="block text-sm font-black uppercase tracking-widest">
                {t.regularization.upTo}
              </span>

              <span
                style={DISPLAY_STYLE}
                className="sc-payment-number mt-3 block font-black tracking-[-0.07em]"
              >
                12
              </span>
            </div>

            <h3
              style={DISPLAY_STYLE}
              className="sc-payment-title sc-spaced font-black uppercase tracking-tight text-[#1648EE]"
            >
              {t.regularization.installments}
            </h3>

            <p className="sc-payment-divider border-t border-[#081B3E]/15 text-sm font-bold">
              {t.regularization.cards}
            </p>

            <p className="mt-2 text-sm leading-6 text-slate-600">
              {t.regularization.fees}
            </p>

            <p className="sc-payment-divider border-t border-[#081B3E]/15 text-xs text-slate-600">
              {t.regularization.conditions}
            </p>
          </div>
        </div>
      </section>

      {/* ==================================================
         PROCESO
      ================================================== */}

      <section
        id="proceso"
        className="sc-container sc-section sc-split sc-process grid"
      >
        <div className="min-w-0">
          <Eyebrow>{t.process.eyebrow}</Eyebrow>
          <AccentRule />

          <h2
            style={DISPLAY_STYLE}
            className="sc-title sc-spaced font-black uppercase tracking-[-0.035em]"
          >
            {t.process.title}
          </h2>

          <p className="sc-copy max-w-lg text-slate-600">
            {t.process.description}
          </p>
        </div>

        <div className="min-w-0 border-t border-[#081B3E]/15">
          {t.process.steps.map((step, index) => (
            <ProcessStep
              key={index}
              index={index + 1}
              {...step}
            />
          ))}
        </div>
      </section>

      {/* ==================================================
         EQUIPO
      ================================================== */}

      <section
        id="equipo"
        className="scroll-mt-24 bg-[#081B3E] text-white"
      >
        <div className="sc-container sc-section sc-split grid items-center">
          <div className="min-w-0">
            <Eyebrow light>{t.team.eyebrow}</Eyebrow>
            <AccentRule />

            <h2
              style={DISPLAY_STYLE}
              className="sc-title sc-spaced font-black uppercase tracking-[-0.035em]"
            >
              {t.team.title}
            </h2>

            <p className="sc-copy text-blue-100/80">
              {t.team.description}
            </p>
          </div>

          <div className="grid min-w-0 gap-5">
            {TEAM.map((member) => (
              <TeamCard
                key={member.id}
                member={member}
                role={t.team.roles[member.id]}
                copy={t.team}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ==================================================
         CONTACTO / FORMULARIO ORIGINAL
      ================================================== */}

      <section
        id="asesoria"
        className="sc-container sc-section"
      >
        <div className="sc-split sc-contact grid items-start">
          <div className="min-w-0">
            <Eyebrow>{t.contact.eyebrow}</Eyebrow>
            <AccentRule />

            <h2
              style={DISPLAY_STYLE}
              className="sc-title sc-spaced font-black uppercase tracking-[-0.035em]"
            >
              {t.contact.title}
            </h2>

            <p className="sc-copy max-w-xl text-slate-600">
              {t.contact.description}
            </p>

            <ul className="sc-spaced grid gap-4 sm:grid-cols-2">
              {t.contact.benefits.map((benefit) => (
                <li
                  key={benefit}
                  className="flex items-start gap-3 text-sm leading-6"
                >
                  <Check
                    aria-hidden="true"
                    className="mt-1 h-4 w-4 shrink-0 text-[#1648EE]"
                  />

                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="sc-panel min-w-0 rounded-3xl border border-[#081B3E]/10 bg-white shadow-xl shadow-blue-950/5">
            <div className="sc-form-heading border-b border-[#081B3E]/10">
              <h3 className="text-xl font-bold">
                {t.contact.formTitle}
              </h3>

              <p className="mt-3 text-sm leading-6 text-slate-600">
                {t.contact.formDescription}
              </p>
            </div>

            {/*
              Conservamos el componente y su funcionamiento.

              language queda preparado para traducir sus campos
              cuando actualicemos ProspectForm.

              No usamos key={language}: así el formulario no se
              reinicia al cambiar de idioma.
            */}
            <ProspectForm language={language} />
          </div>
        </div>
      </section>
    </div>
  );
};

export default MainScreen;